import { notification } from "antd";
import { EventEmitter } from "events";

import { HandleError } from "./helpers";

// Types for MQTT device data
export interface MqttDeviceData {
  id: number;
  serial: string;
  lastReadingAt: number; // Unix timestamp
  jsonData: string;
  validData: boolean; // Boolean flag indicating if data is valid
  active: boolean; // Boolean flag indicating if device is active/deployed
  createdAt: number; // Unix timestamp
}

export interface MqttDeviceResponse {
  success: boolean;
  data?: MqttDeviceData;
  message: string;
}

export interface MqttDeviceListResponse {
  success: boolean;
  data?: MqttDeviceData[];
  message: string;
}

export interface MqttDeviceSerialsResponse {
  success: boolean;
  serials?: string[];
  message: string;
}

export interface DeviceFailure {
  id: string;
  name: string;
  location: string;
  lastSeen: string;
  status: 'offline' | 'online-success' | 'online-invalid' | 'inactive';
  error: string;
}

export interface DashboardStats {
  totalActive: number;
  totalDeployed: number;
  failureRate: number;
  totalOffline: number;
  totalInactive: number;
  totalOnlineInvalid: number;
  lastUpdated: Date;
  failureList: DeviceFailure[];
}

class MqttDeviceStore extends EventEmitter {
  private httpBaseUrl: string;

  constructor() {
    super();
    // Base URL for Flask API service - using Vite proxy for development
    // Production: 'https://agristack.bmplogic.net/flask'
    // Development: '/flask' (proxied to localhost:5000)
    this.httpBaseUrl = '/flask';
    
    // Debug: Log the base URL to see what's being used
    console.log('MqttDeviceStore httpBaseUrl:', this.httpBaseUrl);
    
    // Test server connectivity on initialization
    this.testServerConnectivity();
  }

  // Test if the server is reachable
  private testServerConnectivity = async () => {
    try {
      const response = await fetch(`${this.httpBaseUrl}/health`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      console.log('Flask server connectivity test:', response.status);
    } catch (error) {
      console.warn('Flask server connectivity test failed:', error);
      console.log('Please ensure the Flask data service is running on port 5000');
    }
  };

  // Get list of unique serial numbers
  getUniqueSerials = async (callbackFunc: (serials: string[]) => void) => {
    try {
      console.log('Making HTTP request to Flask API for unique serials');
      console.log('Full URL being requested:', `${this.httpBaseUrl}/api/get_unique_serials`);
      
      const response = await fetch(`${this.httpBaseUrl}/api/get_unique_serials`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        callbackFunc(data.serials || []);
      } else {
        console.error('Failed to get unique serials:', data.message);
        notification.error({
          message: "Error",
          description: data.message,
          duration: 3,
        });
        callbackFunc([]);
      }
    } catch (error: any) {
      console.error('HTTP error getting unique serials:', error);
      notification.error({
        message: "Error",
        description: `Failed to get unique serials: ${error.message || error}`,
        duration: 3,
      });
      callbackFunc([]);
    }
  };

  // Get latest data for a specific serial
  getLatestDataBySerial = async (serial: string, callbackFunc: (data: MqttDeviceData | null) => void) => {
    try {
      const response = await fetch(`${this.httpBaseUrl}/api/get_latest_data_by_serial/${encodeURIComponent(serial)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          console.warn('No latest data found for serial:', serial);
          callbackFunc(null);
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.data) {
        
        // Handle both string and object jsonData
        let jsonDataString: string;
        if (typeof data.data.jsonData === 'string') {
          jsonDataString = data.data.jsonData;
        } else {
          jsonDataString = JSON.stringify(data.data.jsonData);
        }
        
        const mqttData: MqttDeviceData = {
          id: data.data.id,
          serial: data.data.serial,
          lastReadingAt: data.data.lastReadingAt,
          jsonData: jsonDataString,
          validData: data.data.validData === 't' || data.data.validData === true, // Convert 't'/'f' to boolean
          active: data.data.active === 't' || data.data.active === true, // Convert 't'/'f' to boolean
          createdAt: data.data.createdAt
        };
        callbackFunc(mqttData);
      } else {
        console.warn('No latest data found for serial:', serial);
        callbackFunc(null);
      }
    } catch (error: any) {
      console.error('HTTP error getting latest data by serial:', error);
      notification.error({
        message: "Error",
        description: `Failed to get latest data for ${serial}: ${error.message || error}`,
        duration: 3,
      });
      callbackFunc(null);
    }
  };

  // Get historical data for a specific serial
  getDataBySerial = async (serial: string, limit: number = 100, callbackFunc: (data: MqttDeviceData[]) => void) => {
    try {
      const response = await fetch(`${this.httpBaseUrl}/api/get_data_by_serial/${encodeURIComponent(serial)}?limit=${limit}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        const mqttDataList: MqttDeviceData[] = data.data.map((sensorData: any) => {
          
          // Handle both string and object jsonData
          let jsonDataString: string;
          if (typeof sensorData.jsonData === 'string') {
            jsonDataString = sensorData.jsonData;
          } else {
            jsonDataString = JSON.stringify(sensorData.jsonData);
          }
          
          return {
            id: sensorData.id,
            serial: sensorData.serial,
            lastReadingAt: sensorData.lastReadingAt,
            jsonData: jsonDataString,
            validData: sensorData.validData === 't' || sensorData.validData === true, // Convert 't'/'f' to boolean
            active: sensorData.active === 't' || sensorData.active === true, // Convert 't'/'f' to boolean
            createdAt: sensorData.createdAt
          };
        });
        callbackFunc(mqttDataList);
      } else {
        console.error('Failed to get data by serial:', data.message);
        notification.error({
          message: "Error",
          description: data.message,
          duration: 3,
        });
        callbackFunc([]);
      }
    } catch (error: any) {
      console.error('HTTP error getting data by serial:', error);
      notification.error({
        message: "Error",
        description: `Failed to get data for ${serial}: ${error.message || error}`,
        duration: 3,
      });
      callbackFunc([]);
    }
  };

  // Get data by ID
  getDataById = async (id: number, callbackFunc: (data: MqttDeviceData | null) => void) => {
    try {
      const response = await fetch(`${this.httpBaseUrl}/api/get_data_by_id/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          console.warn('No data found for ID:', id);
          callbackFunc(null);
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.data) {
        
        // Handle both string and object jsonData
        let jsonDataString: string;
        if (typeof data.data.jsonData === 'string') {
          jsonDataString = data.data.jsonData;
        } else {
          jsonDataString = JSON.stringify(data.data.jsonData);
        }
        
        const mqttData: MqttDeviceData = {
          id: data.data.id,
          serial: data.data.serial,
          lastReadingAt: data.data.lastReadingAt,
          jsonData: jsonDataString,
          validData: data.data.validData === 't' || data.data.validData === true, // Convert 't'/'f' to boolean
          active: data.data.active === 't' || data.data.active === true, // Convert 't'/'f' to boolean
          createdAt: data.data.createdAt
        };
        callbackFunc(mqttData);
      } else {
        console.warn('No data found for ID:', id);
        callbackFunc(null);
      }
    } catch (error: any) {
      console.error('HTTP error getting data by ID:', error);
      notification.error({
        message: "Error",
        description: `Failed to get data for ID ${id}: ${error.message || error}`,
        duration: 3,
      });
      callbackFunc(null);
    }
  };

  // Save data (for future use if needed)
  saveData = async (serial: string, jsonData: any, lastReadingAt?: number) => {
    try {
      const payload = {
        serial,
        json_data: jsonData,
        last_reading_at: lastReadingAt
      };

      const response = await fetch(`${this.httpBaseUrl}/api/save_data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        console.log('Data saved successfully:', data.record_id);
        return data.record_id;
      } else {
        throw new Error(data.message || 'Failed to save data');
      }
    } catch (error: any) {
      console.error('HTTP error saving data:', error);
      notification.error({
        message: "Error",
        description: `Failed to save data: ${error.message || error}`,
        duration: 3,
      });
      throw error;
    }
  };

  // Delete data (for future use if needed)
  deleteData = async (id: number) => {
    try {
      const response = await fetch(`${this.httpBaseUrl}/api/delete_data/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Record not found');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        console.log('Data deleted successfully:', id);
        return true;
      } else {
        throw new Error(data.message || 'Failed to delete data');
      }
    } catch (error: any) {
      console.error('HTTP error deleting data:', error);
      notification.error({
        message: "Error",
        description: `Failed to delete data: ${error.message || error}`,
        duration: 3,
      });
      throw error;
    }
  };

  // Get dashboard statistics
  getDashboardStats = async (callbackFunc: (stats: DashboardStats) => void) => {
    try {
      // Get unique serials first
      await this.getUniqueSerials(async (serials) => {
        const validSerials = serials.filter(serial => 
          serial && serial.trim() !== '' && serial !== 'null' && serial !== 'undefined'
        );
        
        let totalDeployed = 0; // Count only active devices
        let totalActive = 0; // Count devices with "Online - Success" status
        let totalOffline = 0; // Count devices with "Offline" status
        let totalInactive = 0; // Count devices with "Inactive" status
        let totalOnlineInvalid = 0; // Count devices with "Online - Invalid" status
        let failureList: DeviceFailure[] = [];
        
        // Check each device's status
        const devicePromises = validSerials.map(serial => 
          new Promise<void>((resolve) => {
            // Get all data for the last 24 hours to check valid_data status
            this.getDataBySerial(serial, 100, (allData) => {
              const now = Math.floor(Date.now() / 1000);
              const twentyFourHoursAgo = now - 86400; // 24 hours in seconds
              
              // Filter data from last 24 hours
              const recentData = allData.filter(data => data.lastReadingAt >= twentyFourHoursAgo);
              
              let status: 'offline' | 'online-success' | 'online-invalid' | 'inactive';
              let error = '';
              
              // Check if device is active in database using the most recent data
              const mostRecentData = recentData.length > 0 ? recentData[0] : null;
              const isDeviceActive = mostRecentData ? mostRecentData.active : false;
              
              if (!isDeviceActive) {
                // Device is inactive in database
                status = 'inactive';
                error = 'Device marked as inactive in database';
                totalInactive++; // Count inactive devices
                
                // Get latest data for location info
                this.getLatestDataBySerial(serial, (latestData) => {
                  failureList.push({
                    id: serial,
                    name: `Device ${serial}`,
                    location: this.extractLocation(latestData),
                    lastSeen: 'N/A',
                    status,
                    error
                  });
                  resolve();
                });
              } else {
                // Device is active - count as deployed
                totalDeployed++;
                
                if (recentData.length === 0) {
                  // No packets in last 24 hours AND device is active - Offline
                  status = 'offline';
                  error = 'No packets received in last 24 hours';
                  totalOffline++; // Count offline devices
                  
                  // Get latest data for last seen info and location
                  this.getLatestDataBySerial(serial, (latestData) => {
                    failureList.push({
                      id: serial,
                      name: `Device ${serial}`,
                      location: this.extractLocation(latestData),
                      lastSeen: latestData ? this.formatLastSeen(latestData.lastReadingAt) : 'Never',
                      status,
                      error
                    });
                    resolve();
                  });
                } else {
                  // Has packets in last 24 hours - check most recent packet's valid_data
                  const mostRecentData = recentData[0]; // Most recent packet
                  
                  if (mostRecentData.validData === true) {
                    // Most recent packet has valid_data = true - Online Success
                    status = 'online-success';
                    totalActive++; // Count as active
                    error = 'Device online with valid data';
                  } else {
                    // Most recent packet has valid_data = false - Online Invalid
                    status = 'online-invalid';
                    totalOnlineInvalid++; // Count devices with invalid data
                    error = 'Device online but most recent packet is invalid';
                  }
                  
                  // Add to failure list if not online-success
                  if (status !== 'online-success') {
                    failureList.push({
                      id: serial,
                      name: `Device ${serial}`,
                      location: this.extractLocation(mostRecentData),
                      lastSeen: this.formatLastSeen(mostRecentData.lastReadingAt),
                      status,
                      error
                    });
                  }
                  resolve();
                }
              }
            });
          })
        );
        
        await Promise.all(devicePromises);
        
        const failureRate = totalDeployed > 0 ? ((totalDeployed - totalActive) / totalDeployed) * 100 : 0;
        
        const stats: DashboardStats = {
          totalActive,
          totalDeployed,
          failureRate: Math.round(failureRate * 10) / 10, // Round to 1 decimal place
          totalOffline,
          totalInactive,
          totalOnlineInvalid,
          lastUpdated: new Date(),
          failureList: failureList.slice(0, 10) // Limit to top 10 failures
        };
        
        callbackFunc(stats);
      });
    } catch (error: any) {
      console.error('Error getting dashboard stats:', error);
      // Return default stats on error
      callbackFunc({
        totalActive: 0,
        totalDeployed: 0,
        failureRate: 0,
        totalOffline: 0,
        totalInactive: 0,
        totalOnlineInvalid: 0,
        lastUpdated: new Date(),
        failureList: []
      });
    }
  };

  // Helper method to format last seen time
  private formatLastSeen = (timestamp: number): string => {
    const now = Math.floor(Date.now() / 1000);
    const diff = now - timestamp;
    
    if (diff < 3600) { // Less than 1 hour
      const minutes = Math.floor(diff / 60);
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    } else if (diff < 86400) { // Less than 1 day
      const hours = Math.floor(diff / 3600);
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else { // More than 1 day
      const days = Math.floor(diff / 86400);
      return `${days} day${days !== 1 ? 's' : ''} ago`;
    }
  };

  // Helper method to recursively search for latitude and longitude in nested objects
  private findLatLngRecursively = (obj: any, path: string = ''): { lat: any, lng: any, path: string } | null => {
    if (obj === null || obj === undefined) return null;
    
    // Check if current object has latitude and longitude
    if (obj.latitude !== undefined && obj.longitude !== undefined) {
      return { lat: obj.latitude, lng: obj.longitude, path };
    }
    
    // If it's an object, recursively search its properties
    if (typeof obj === 'object' && !Array.isArray(obj)) {
      for (const [key, value] of Object.entries(obj)) {
        const currentPath = path ? `${path}.${key}` : key;
        const result = this.findLatLngRecursively(value, currentPath);
        if (result) return result;
      }
    }
    
    // If it's an array, search each element
    if (Array.isArray(obj)) {
      for (let i = 0; i < obj.length; i++) {
        const currentPath = `${path}[${i}]`;
        const result = this.findLatLngRecursively(obj[i], currentPath);
        if (result) return result;
      }
    }
    
    return null;
  };

  // Helper method to extract location from device data
  private extractLocation = (data: MqttDeviceData | null): string => {
    if (!data) return "Unknown";
    
    try {
      const parsedData = JSON.parse(data.jsonData);
      
      // Recursively search for latitude and longitude
      const location = this.findLatLngRecursively(parsedData);
      
      if (location) {
        return `${location.lat}, ${location.lng}`;
      }
      
      return "Unknown";
    } catch (error) {
      console.error('Error parsing JSON data for location:', error);
      return "Unknown";
    }
  };

  // Mark device as inactive by serial
  markDeviceInactive = async (serial: string): Promise<boolean> => {
    try {
      console.log('Marking device as inactive:', serial);
      
      const response = await fetch(`${this.httpBaseUrl}/api/mark_inactive_by_serial/${encodeURIComponent(serial)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        console.log('Device marked as inactive successfully:', serial);
        notification.success({
          message: "Success",
          description: `Device ${serial} has been marked as inactive`,
          duration: 3,
        });
        return true;
      } else {
        throw new Error(data.message || 'Failed to mark device as inactive');
      }
    } catch (error: any) {
      console.error('Error marking device as inactive:', error);
      notification.error({
        message: "Error",
        description: `Failed to mark device ${serial} as inactive: ${error.message || error}`,
        duration: 3,
      });
      return false;
    }
  };

  // Mark device as active by serial
  markDeviceActive = async (serial: string): Promise<boolean> => {
    try {
      console.log('Marking device as active:', serial);
      
      const response = await fetch(`${this.httpBaseUrl}/api/mark_active_by_serial/${encodeURIComponent(serial)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        console.log('Device marked as active successfully:', serial);
        notification.success({
          message: "Success",
          description: `Device ${serial} has been marked as active`,
          duration: 3,
        });
        return true;
      } else {
        throw new Error(data.message || 'Failed to mark device as active');
      }
    } catch (error: any) {
      console.error('Error marking device as active:', error);
      notification.error({
        message: "Error",
        description: `Failed to mark device ${serial} as active: ${error.message || error}`,
        duration: 3,
      });
      return false;
    }
  };
}

const mqttDeviceStore = new MqttDeviceStore();
export default mqttDeviceStore;